import { Controller } from "@hotwired/stimulus"

// Expand/collapse the "likelihood to qualify" banner's detail. Toggling the `expanded` class on the
// detail target animates its height via a CSS grid-template-rows transition (0fr <-> 1fr), so the banner
// visibly GROWS to its new size rather than snapping. See _qualification_banner.html.erb and the
// .qualification-detail-wrap rules in application.tailwind.css.
//
// Also records impressions: a fire-and-forget ping when the banner connects ("shown") -- which covers both
// the page render and a banner that arrives later via the Turbo broadcast -- and on the first expand
// ("expanded"). The event URL is only rendered when rendering as the application's own member. The server de-dups to one
// activity per (application, state) and records only if `state` (what's on screen) matches its own view;
// sessionStorage just avoids re-pinging on every page view in this tab, and is only marked after a 2xx so a
// failed ping (expired session, deploy blip) is retried on the next page view.
export default class extends Controller {
  static targets = ["detail", "toggle"]
  static values = { eventUrl: String, state: String, impressionKey: String }

  connect() {
    this.ping("shown")
    // An expand ping that failed (e.g. lock busy, network) is retried on the next page view, not only when
    // the member happens to expand again.
    if (this.readStorage(this.storageKey("expanded:pending"))) this.ping("expanded")
  }

  // Bound to click + Enter/Space on the header row (the whole collapsed bar). preventDefault on keydown so
  // Space doesn't scroll the page when the row is focused.
  toggle(event) {
    if (event && event.type === "keydown") event.preventDefault()
    if (!this.hasDetailTarget) return
    const expanded = this.detailTarget.classList.toggle("expanded")
    if (this.hasToggleTarget) this.toggleTarget.setAttribute("aria-expanded", expanded ? "true" : "false")
    if (expanded) this.ping("expanded")
  }

  // Best-effort only: never throws, never blocks the banner.
  ping(eventName) {
    if (!this.hasEventUrlValue || !this.eventUrlValue) return
    const key = this.storageKey(eventName)
    if (this.readStorage(key)) return

    const pendingKey = this.storageKey(`${eventName}:pending`)
    this.writeStorage(pendingKey)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ""
    try {
      fetch(this.eventUrlValue, {
        method: "POST",
        // Accept JSON so a signed-out ping gets a 401 rather than a redirect to the (200) sign-in page.
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken },
        body: JSON.stringify({ event: eventName, state: this.stateValue }),
      })
        .then((response) => {
          if (!response.ok || response.redirected) return
          this.writeStorage(key)
          this.removeStorage(pendingKey)
        })
        .catch(() => {})
    } catch (_) {
      // fetch unavailable -- nothing to record
    }
  }

  storageKey(eventName) {
    return `qualification-banner:${this.impressionKeyValue}:${eventName}`
  }

  // Storage can throw (privacy modes); then we just ping again and let the server de-dup.
  readStorage(key) {
    try {
      return window.sessionStorage.getItem(key) !== null
    } catch (_) {
      return false
    }
  }

  writeStorage(key) {
    try {
      window.sessionStorage.setItem(key, "1")
    } catch (_) {
      // ignore
    }
  }

  removeStorage(key) {
    try {
      window.sessionStorage.removeItem(key)
    } catch (_) {
      // ignore
    }
  }
}
